import { $jsonApi } from '#/api/client'
import type { components } from '#/api/schema'

type TPendingApprovalsList = components['schemas']['PendingApprovalsList']
type TPropositionGroup = TPendingApprovalsList['propositions'][number]

export interface IPendingPropositionRequestedBy {
  firstName: string | null
  lastName: string | null
  email: string | null
}

export interface IPendingProposition {
  itemIds: string[]
  companyId: string | null
  name: string | null
  companyName: string | null
  category: string | null
  approvalStatus: string | null
  approvalRequestedAt: string | null
  approvalRequestedBy: IPendingPropositionRequestedBy | null
  quantity: number
  budgetAmountInCents: number
  budgetType: string | null
}

interface ISelectResult {
  propositions: IPendingProposition[]
  totalPendingPropositions: number
}

interface IFetchPendingApprovalsResponse {
  propositions: IPendingProposition[]
  totalPendingPropositions: number
  isLoading: boolean
  isError: boolean
}

function mapProposition(item: TPropositionGroup): IPendingProposition {
  const firstItem = item.items?.[0]

  return {
    itemIds: (item.items ?? []).map((i) => i.id ?? '').filter(Boolean),
    companyId: item.companyId ?? null,
    name: firstItem?.name ?? null,
    companyName: item.companyName ?? null,
    category: item.category ?? null,
    approvalStatus: 'pending',
    approvalRequestedAt: item.approvalRequestedAt ?? null,
    approvalRequestedBy: item.requestedBy
      ? {
          firstName: item.requestedBy.firstName ?? null,
          lastName: item.requestedBy.lastName ?? null,
          email: item.requestedBy.email ?? null,
        }
      : null,
    quantity: item.quantity ?? 0,
    budgetAmountInCents: firstItem?.budget?.amountInCents ?? 0,
    budgetType: firstItem?.budget?.periodType ?? null,
  }
}

export const useFetchPendingApprovals = (): IFetchPendingApprovalsResponse => {
  const { data, isLoading, isError } = $jsonApi.useQuery(
    'get',
    '/approvals/pending',
    {},
    {
      select: (response: TPendingApprovalsList): ISelectResult => ({
        propositions: (response.propositions ?? []).map(mapProposition),
        totalPendingPropositions: response.totalPendingPropositions,
      }),
    },
  )

  return {
    propositions: data?.propositions ?? [],
    totalPendingPropositions: data?.totalPendingPropositions ?? 0,
    isLoading,
    isError,
  }
}
